import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {RoleEntity} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {IconEdit, IconTrash} from "@tabler/icons-react";
import {useRoleAction} from "./useRoleAction";

const ALL_ROLE_ACTIONS = [
  "EDIT",
  "DELETE",
] as const;

export type RoleAction = typeof ALL_ROLE_ACTIONS[number];

export type RoleActionMenuDropdownProps = {
  role: RoleEntity
  actions?: RoleAction[]
  onActionClick?: (action: RoleAction) => void
  showEmpty?: boolean
}

export const RoleActionMenuDropdown = (props: RoleActionMenuDropdownProps) => {
  const {
    role,
    actions = ALL_ROLE_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<RoleAction> = useMemo(() => {
    const enabledActions = new Set<RoleAction>()
    const actionsSet = actions ? new Set<RoleAction>(actions) : new Set<RoleAction>()

    if (hasAccess("role_edit")) {
      if (actionsSet.has("EDIT")) {
        enabledActions.add("EDIT")
      }
    }

    if (hasAccess("role_delete")) {
      if (actionsSet.has("DELETE")) {
        enabledActions.add("DELETE")
      }
    }

    return enabledActions
  }, [role, actions, hasAccess])

  const {
    onRoleActionClick
  } = useRoleAction()

  const _onActionClick = useCallback((action: RoleAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onRoleActionClick(role, action)
    }
  }, [onActionClick, onRoleActionClick, role])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item icon={<IconEdit size={14}/>} onClick={() => _onActionClick("EDIT")}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("DELETE") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("DELETE")}
            icon={<IconTrash size={14}/>}
            color="red">
            {t("common.action.delete", "Delete")}
          </Menu.Item>
        </>
      }
    </>
  )
}
